/* eslint-disable no-unused-vars */
import React, {useEffect, useCallback, useRef} from 'react'
import {useNavigate, useLocation, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {KTCard, KTCardBody, Select, Loading} from '../../../components'

import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {warehouseUpdate, warehouseGetId} from '../../../redux/slices/product.slice'
import {basicLookup, projectLookup, districtLookup} from '../../../redux/slices/lookup.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs,
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  Province: state.lookup.Province,
  District: state.lookup.District,
  projectLookupList: state.lookup.projectLookupList,
  loading: state.lookup.loading,
})

const selectProductData = (state) => ({
  isRedirect: state.product.isRedirect,
  detail: state.product.detail,
  loading: state.product.loading,
})

function Edit() {
  const intl = useIntl()
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)
  const formikRef = useRef()
  const isFormPopulatedRef = useRef(false)

  const {Province, District, projectLookupList, loading} = useSelector(selectLookupData)
  const {isRedirect, detail, loading: updateLoading} = useSelector(selectProductData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(basicLookup('Province'))
    dispatch(projectLookup())
    dispatch(warehouseGetId(id))
  }, [dispatch, id])

  const validSchema = useMemoizedValidationSchema(
    () =>
      Yup.object().shape({
        Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      }),
    [intl]
  )

  const formValues = useMemoizedFormValues({
    Id: '',
    Name: '',
    ProjectId: null,
    ProvinceId: '',
    DistrictId: '',
    Address: '',
  })

  const handleSubmit = useCallback(
    async (values, {setSubmitting}) => {
      setSubmitting(true)
      dispatch(warehouseUpdate(values))
    },
    [dispatch]
  )

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  })

  formikRef.current = formik

  const onChangeDistrict = useCallback(
    (value) => {
      formik.setFieldValue('ProvinceId', value)
      dispatch(districtLookup({provinceId: value}))
    },
    [formik, dispatch]
  )

  useEffect(() => {
    if (detail && !isFormPopulatedRef.current) {
      if (detail.provinceId) {
        dispatch(districtLookup({provinceId: detail.provinceId}))
      }

      const populatedValues = {
        Id: detail.id || '',
        Name: detail.name || '',
        ProjectId: detail.projectId || null,
        ProvinceId: detail.provinceId || '',
        DistrictId: detail.districtId || '',
        Address: detail.address || '',
      }

      formik.setValues(populatedValues)
      isFormPopulatedRef.current = true
    }
  }, [detail, formik, dispatch])

  const breadcrumbs = useMemoizedBreadcrumbs(
    () => [
      {
        title: intl.formatMessage({id: 'BC.WAREHOUSE'}),
        path: '/stock/warehouse',
        isSeparator: false,
        isActive: false,
      },
      {
        title: intl.formatMessage({id: 'PAGE_TITLE.DETAIL'}),
        path: `/stock/warehouse/detail/${id}`,
        isSeparator: false,
        isActive: false,
      },
      {
        title: '',
        path: '',
        isSeparator: true,
        isActive: false,
      },
    ],
    [intl, id]
  )

  useEffect(() => {
    if (isRedirect) navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)
  }, [isRedirect, navigate, pageUrl, id])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.WAREHOUSE.EDIT'})}
      </PageTitle>
      <KTCard>
        <KTCardBody>
          {detail && (
            <form className='form' onSubmit={formik.handleSubmit} noValidate>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'WAREHOUSE.UPDATE.NAME'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    required
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('Name')}
                  />
                  {formik.touched.Name && formik.errors.Name && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.Name}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'WAREHOUSE.UPDATE.PROJECT'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={projectLookupList}
                    required
                    {...formik.getFieldProps('ProjectId')}
                    onChange={(item) => formik.setFieldValue('ProjectId', Number(item?.value))}
                  />
                  {formik.touched.ProjectId && formik.errors.ProjectId && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.ProjectId}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'WAREHOUSE.UPDATE.PROVINCE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={Province}
                    {...formik.getFieldProps('ProvinceId')}
                    onChange={(item) => onChangeDistrict(Number(item.value))}
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'WAREHOUSE.UPDATE.DISTRICT'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={District}
                    {...formik.getFieldProps('DistrictId')}
                    onChange={(item) => formik.setFieldValue('DistrictId', Number(item?.value))}
                  />
                  {formik.touched.DistrictId && formik.errors.DistrictId && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.DistrictId}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'WAREHOUSE.UPDATE.ADDRESS'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <textarea
                    type='text'
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    rows={2}
                    {...formik.getFieldProps('Address')}
                  />
                </div>
              </div>
              <div className='row pt-15'>
                <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                  <button
                    className='btn btn-light'
                    onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)}
                  >
                    {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    data-kt-users-modal-action='submit'
                    disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                  >
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                    </span>
                    {formik.isSubmitting && (
                      <span className='indicator-progress'>
                        {intl.formatMessage({id: 'PLEASE'})}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </KTCardBody>
      </KTCard>

      {(loading | updateLoading) > 0 && <Loading />}
    </>
  )
}
export {Edit}
